// Stat aggregation for a loadout (Phaser-free).
//
// Final stats = mech base → every additive modifier → every multiplicative
// modifier → clamps. Sources: equipment stats, item affixes (weapons, mods,
// equipment, mutations), mutation stats, major upgrades and active synergies.
// Temporary buffs are layered on top by the player.

import {
  EQUIPMENT,
  MAJOR_UPGRADES,
  MODS,
  MUTATIONS,
  SYNERGIES,
  SYNERGY_DEF_IDS,
  WEAPONS,
} from '../../data/items';
import type {
  EquipInstance,
  EquipSlot,
  ItemInstance,
  MajorUpgradeId,
  ModInstance,
  MutationInstance,
  StatBlock,
  StatKey,
  StatMod,
  StatusApplication,
  SynergyId,
  Tag,
  WeaponInstance,
  WeaponModifier,
} from '../../core/types';

/** Plain-data view of everything a player carries. */
export interface Loadout {
  /** Always length 2. */
  weapons: (WeaponInstance | null)[];
  activeWeapon: number;
  equipment: Record<EquipSlot, EquipInstance | null>;
  /** Oldest first. */
  mutations: MutationInstance[];
  upgrades: MajorUpgradeId[];
}

// ───────────────────────────── Stat presentation ─────────────────────────────

/**
 * How a stat reads to the player.
 * flat  – absolute number (+30 max shield)
 * rel   – relative change in % (+20% cooling)
 * pts   – fraction shown as percentage points (+6% crit chance)
 * count – whole number (+1 chain target)
 * sec   – seconds (-1.2s shield delay)
 */
export type StatMode = 'flat' | 'rel' | 'pts' | 'count' | 'sec';

export interface StatInfo {
  /** Delta label, sentence case ('Max shield'). */
  label: string;
  /** Lower-case noun used in item lines ('max shield'). */
  noun: string;
  /** Plural noun for counts ('chain targets'). */
  plural?: string;
  mode: StatMode;
  lowerIsBetter?: boolean;
}

export const STAT_INFO: Readonly<Record<StatKey, StatInfo>> = {
  maxHp: { label: 'Max HP', noun: 'max HP', mode: 'flat' },
  maxShield: { label: 'Max shield', noun: 'max shield', mode: 'flat' },
  shieldRegen: { label: 'Shield regen', noun: 'shield regen', mode: 'rel' },
  shieldDelay: { label: 'Shield delay', noun: 'shield delay', mode: 'sec', lowerIsBetter: true },
  armor: { label: 'Armor', noun: 'armor', mode: 'pts' },
  moveSpeed: { label: 'Move speed', noun: 'move speed', mode: 'rel' },
  jumpPower: { label: 'Jump power', noun: 'jump power', mode: 'rel' },
  airJumps: { label: 'Air jumps', noun: 'air jump', plural: 'air jumps', mode: 'count' },
  airDashes: { label: 'Air dashes', noun: 'air dash', plural: 'air dashes', mode: 'count' },
  dashCooldown: { label: 'Dash cooldown', noun: 'dash cooldown', mode: 'rel', lowerIsBetter: true },
  dashPower: { label: 'Dash distance', noun: 'dash distance', mode: 'rel' },
  damage: { label: 'Damage', noun: 'damage', mode: 'rel' },
  fireRate: { label: 'Fire rate', noun: 'fire rate', mode: 'rel' },
  heatCapacity: { label: 'Heat capacity', noun: 'heat capacity', mode: 'rel' },
  cooling: { label: 'Cooling', noun: 'cooling', mode: 'rel' },
  critChance: { label: 'Crit chance', noun: 'crit chance', mode: 'pts' },
  critMult: { label: 'Crit damage', noun: 'crit damage', mode: 'pts' },
  signatureCooldown: { label: 'Signature cooldown', noun: 'signature cooldown', mode: 'rel', lowerIsBetter: true },
  ultimateCharge: { label: 'Ultimate charge', noun: 'ultimate charge', mode: 'rel' },
  chainBonus: { label: 'Chain targets', noun: 'chain target', plural: 'chain targets', mode: 'count' },
  chainDamage: { label: 'Chain damage', noun: 'chain damage', mode: 'rel' },
  burnPower: { label: 'Burn damage', noun: 'burn damage', mode: 'rel' },
  explosionRadius: { label: 'Blast radius', noun: 'blast radius', mode: 'rel' },
  explosionDamage: { label: 'Blast damage', noun: 'blast damage', mode: 'rel' },
  knockback: { label: 'Knockback', noun: 'knockback', mode: 'rel' },
  knockbackResist: { label: 'Knockback resist', noun: 'knockback resist', mode: 'pts' },
  pickupRadius: { label: 'Pickup radius', noun: 'pickup radius', mode: 'rel' },
  scrapGain: { label: 'Scrap gain', noun: 'scrap gain', mode: 'rel' },
  damageTaken: { label: 'Damage taken', noun: 'damage taken', mode: 'rel', lowerIsBetter: true },
};

export const STAT_KEYS = Object.keys(STAT_INFO) as StatKey[];

/** '+12' / '-7' / '+1.5' with a real minus sign avoided (ASCII for font safety). */
export function signed(n: number, digits = 0): string {
  const v = Number(n.toFixed(digits));
  if (v === 0) return '+0';
  return `${v > 0 ? '+' : '-'}${Math.abs(v)}`;
}

/** True if this single modifier helps the player. */
export function isBeneficial(mod: StatMod): boolean {
  const info = STAT_INFO[mod.stat];
  const delta = mod.op === 'mul' ? mod.value - 1 : mod.value;
  if (delta === 0) return true;
  return info?.lowerIsBetter ? delta < 0 : delta > 0;
}

/** Item-line text for one modifier: '+20% cooling', '+15 max shield', '+1 chain target'. */
export function formatStatMod(mod: StatMod): string {
  const info = STAT_INFO[mod.stat] ?? { label: String(mod.stat), noun: String(mod.stat), mode: 'flat' as StatMode };
  if (mod.op === 'mul') {
    // ×3 reads better as "3x pickup radius" than "+200% pickup radius".
    if (mod.value >= 2 && Number.isInteger(mod.value)) return `${mod.value}x ${info.noun}`;
    if (mod.value === 0.5) return `Halved ${info.noun}`;
    return `${signed((mod.value - 1) * 100)}% ${info.noun}`;
  }
  switch (info.mode) {
    case 'pts':
      return `${signed(mod.value * 100)}% ${info.noun}`;
    case 'count': {
      const n = Math.round(mod.value);
      return `${signed(n)} ${Math.abs(n) === 1 ? info.noun : info.plural ?? info.noun}`;
    }
    case 'sec':
      return `${signed(mod.value, 1)}s ${info.noun}`;
    case 'rel':
      return `${signed(mod.value * 100)}% ${info.noun}`;
    default:
      return `${signed(mod.value)} ${info.noun}`;
  }
}

// ───────────────────────────── Stat aggregation ─────────────────────────────

/** Every equipped item in a loadout (weapons, their mods, equipment, mutations). */
export function loadoutItems(l: Loadout): ItemInstance[] {
  const out: ItemInstance[] = [];
  for (const w of l.weapons) {
    if (!w) continue;
    out.push(w);
    for (const m of w.mods) out.push(m);
  }
  for (const e of Object.values(l.equipment)) if (e) out.push(e);
  for (const m of l.mutations) out.push(m);
  return out;
}

/** Tags an item contributes to synergy counts (its definition tags). */
export function itemTags(item: ItemInstance): readonly Tag[] {
  switch (item.kind) {
    case 'weapon':
      return WEAPONS[item.defId]?.tags ?? [];
    case 'equipment':
      return EQUIPMENT[item.defId]?.tags ?? [];
    case 'mod':
      return MODS[item.defId]?.tags ?? [];
    case 'mutation':
      return MUTATIONS[item.defId]?.tags ?? [];
  }
}

/** Each item's tags counted once, plus the mech kit tags (each counted once). */
export function tagCountsFor(l: Loadout, kitTags: readonly Tag[] = []): Map<Tag, number> {
  const counts = new Map<Tag, number>();
  const add = (tags: Iterable<Tag>) => {
    for (const t of new Set(tags)) counts.set(t, (counts.get(t) ?? 0) + 1);
  };
  add(kitTags);
  for (const item of loadoutItems(l)) add(itemTags(item));
  return counts;
}

export function synergyMet(requires: Partial<Record<Tag, number>>, counts: ReadonlyMap<Tag, number>): boolean {
  for (const [tag, need] of Object.entries(requires) as [Tag, number][]) {
    if ((counts.get(tag) ?? 0) < need) return false;
  }
  return true;
}

export function activeSynergiesFor(l: Loadout, kitTags: readonly Tag[] = []): SynergyId[] {
  const counts = tagCountsFor(l, kitTags);
  return SYNERGY_DEF_IDS.filter((id) => synergyMet(SYNERGIES[id].requires, counts));
}

/** Every StatMod the loadout grants (before ordering). */
export function collectStatMods(l: Loadout, kitTags: readonly Tag[] = []): StatMod[] {
  const mods: StatMod[] = [];
  const push = (list: readonly StatMod[] | undefined) => {
    if (list) for (const m of list) mods.push(m);
  };
  for (const item of loadoutItems(l)) {
    if (item.kind === 'equipment') push(EQUIPMENT[item.defId]?.stats);
    else if (item.kind === 'mutation') push(MUTATIONS[item.defId]?.stats);
    for (const a of item.affixes) push(a.stats);
  }
  for (const id of l.upgrades) push(MAJOR_UPGRADES[id]?.stats);
  for (const id of activeSynergiesFor(l, kitTags)) push(SYNERGIES[id].stats);
  return mods;
}

/** Base → all adds → all muls (order-independent within each phase). */
export function applyStatMods(base: StatBlock, mods: readonly StatMod[]): StatBlock {
  const s: StatBlock = { ...base };
  for (const m of mods) if (m.op === 'add' && m.stat in s) s[m.stat] += m.value;
  for (const m of mods) if (m.op === 'mul' && m.stat in s) s[m.stat] *= m.value;
  return s;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Design clamps (armor ≤ 0.6, crit ≤ 0.75, damageTaken ≥ 0.4, dashCooldown ≥ 0.35 ...) plus sanity floors. */
export function clampStats(s: StatBlock): StatBlock {
  s.armor = clamp(s.armor, 0, 0.6);
  s.critChance = clamp(s.critChance, 0, 0.75);
  s.damageTaken = Math.max(0.4, s.damageTaken);
  s.dashCooldown = Math.max(0.35, s.dashCooldown);
  s.maxShield = Math.max(0, s.maxShield);
  s.airJumps = Math.max(0, Math.round(s.airJumps));
  // Sanity floors that never bind in normal play but keep the sim well-defined.
  s.airDashes = Math.max(0, Math.round(s.airDashes));
  s.chainBonus = Math.max(0, Math.round(s.chainBonus));
  s.maxHp = Math.max(1, s.maxHp);
  s.knockbackResist = clamp(s.knockbackResist, 0, 1);
  s.shieldRegen = Math.max(0, s.shieldRegen);
  s.shieldDelay = Math.max(0.3, s.shieldDelay);
  s.signatureCooldown = Math.max(0.25, s.signatureCooldown);
  for (const k of ['moveSpeed', 'jumpPower', 'dashPower', 'fireRate', 'heatCapacity', 'cooling'] as const) {
    s[k] = Math.max(0.2, s[k]);
  }
  for (const k of ['damage', 'critMult', 'ultimateCharge', 'chainDamage', 'burnPower', 'explosionRadius', 'explosionDamage', 'knockback', 'pickupRadius', 'scrapGain'] as const) {
    s[k] = Math.max(0, s[k]);
  }
  return s;
}

export function computeStatsFor(l: Loadout, base: StatBlock, kitTags: readonly Tag[] = []): StatBlock {
  return clampStats(applyStatMods(base, collectStatMods(l, kitTags)));
}

// ───────────────────────────── Weapon modifiers ─────────────────────────────

/** Every installed mod folded into one modifier (multipliers multiply, additions add). */
export interface CombinedModifier {
  damageMult: number;
  fireRateMult: number;
  heatMult: number;
  coolMult: number;
  projectileSpeedMult: number;
  knockbackMult: number;
  spreadAdd: number;
  pierceAdd: number;
  extraProjectiles: number;
  burst?: { count: number; interval: number };
  addTags: Tag[];
  addStatuses: StatusApplication[];
  explodeOnKill?: { radius: number; damageMult: number };
}

export function combineModifiers(mods: readonly (ModInstance | WeaponModifier)[]): CombinedModifier {
  const c: CombinedModifier = {
    damageMult: 1,
    fireRateMult: 1,
    heatMult: 1,
    coolMult: 1,
    projectileSpeedMult: 1,
    knockbackMult: 1,
    spreadAdd: 0,
    pierceAdd: 0,
    extraProjectiles: 0,
    addTags: [],
    addStatuses: [],
  };
  for (const entry of mods) {
    const w: WeaponModifier | undefined = 'kind' in entry ? MODS[(entry as ModInstance).defId]?.weapon : (entry as WeaponModifier);
    if (!w) continue;
    c.damageMult *= w.damageMult ?? 1;
    c.fireRateMult *= w.fireRateMult ?? 1;
    c.heatMult *= w.heatMult ?? 1;
    c.coolMult *= w.coolMult ?? 1;
    c.projectileSpeedMult *= w.projectileSpeedMult ?? 1;
    c.knockbackMult *= w.knockbackMult ?? 1;
    c.spreadAdd += w.spreadAdd ?? 0;
    c.pierceAdd += w.pierceAdd ?? 0;
    c.extraProjectiles += w.extraProjectiles ?? 0;
    if (w.burst && (!c.burst || w.burst.count > c.burst.count)) c.burst = { ...w.burst };
    for (const t of w.addTags ?? []) if (!c.addTags.includes(t)) c.addTags.push(t);
    for (const s of w.addStatuses ?? []) c.addStatuses.push({ ...s });
    if (w.explodeOnKill && (!c.explodeOnKill || w.explodeOnKill.radius > c.explodeOnKill.radius)) {
      c.explodeOnKill = { ...w.explodeOnKill };
    }
  }
  return c;
}

/** Fraction of fanned split-shot projectiles assumed to hit a single target. */
const EXTRA_PROJECTILE_HIT_RATE = 0.5;

/**
 * Burst DPS estimate against one target at a sensible range (point blank for
 * the shotgun), including installed mods and crits. Heat limits are ignored —
 * this is for comparisons, not a simulation.
 */
export function estimateDps(weapon: WeaponInstance, stats: Readonly<StatBlock>): number {
  const def = WEAPONS[weapon.defId];
  if (!def) return 0;
  const m = combineModifiers(weapon.mods);
  const crit = 1 + clamp(stats.critChance, 0, 1) * Math.max(0, stats.critMult - 1);
  const hit = def.damage * m.damageMult * stats.damage * crit;
  const rate = def.fireRate * m.fireRateMult * stats.fireRate;
  const projectiles = 1 + m.extraProjectiles * EXTRA_PROJECTILE_HIT_RATE;
  const burst = m.burst?.count ?? 1;
  let dps: number;
  switch (def.behavior) {
    case 'spread':
      dps = hit * def.pellets * rate * projectiles * burst;
      break;
    case 'charge': {
      const cycle = (def.chargeTime ?? 0.7) / Math.max(0.2, stats.fireRate) + 1 / Math.max(0.01, rate);
      dps = (hit * projectiles * burst) / cycle;
      break;
    }
    case 'missile_burst': {
      const count = Math.max(def.burst?.count ?? 1, burst);
      const blast = (def.explode?.damageMult ?? 0) * stats.explosionDamage;
      dps = hit * (1 + blast) * count * rate * projectiles;
      break;
    }
    case 'lobber': {
      const blast = (def.explode?.damageMult ?? 0) * stats.explosionDamage;
      const implode = def.field?.implodeDamageMult ?? 0;
      dps = hit * (1 + blast + implode) * rate * projectiles * burst;
      break;
    }
    default:
      // auto / beam_chain: the primary target only (chain jumps are a bonus on groups).
      dps = hit * rate * projectiles * burst;
  }
  return Math.max(0, dps);
}
